import React from 'react';
import moment from 'moment';
import { View, Text, Image } from 'react-native';
import { createReducer, createAction } from '@reduxjs/toolkit';
import DeviceInfo from 'react-native-device-info';
import ReactNativeBlobUtil from 'react-native-blob-util';
import RNFS from 'react-native-fs';
import { BlobServiceClient, BlockBlobClient } from '@azure/storage-blob';
import i18n from 'i18n-js';

import { backendCall, eventCall } from './utils';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import { isWeb, Alert, isAndroid, toBase64, imageSource, getSecureValue, isExpiredTimestamp, setExpirationTimeAsTimestamp } from '@helpers';
import { scale } from '@components/frame';

import Svg from '@styles/icons/svg';

import {
  generateImages,
  TAG_KEYS,
  formatDate,
  favoriteKey,
  limitMinimumSize,
  smallImageHeight,
  DOCUMENT_RELATED_IDS_KEYS,
  DOCUMENT_HIERARCHY_CONFIG,
  DOCUMENT_TYPE_TAG_KEY_MAP,
  SCOPE_ID_TO_TAG_NAME,
  DOCUMENTS_STORE_MODE,
  INDIRECT_LINKING_TEXT,
} from '../components/imageHandling/common';

import { startQueue, updateUploadQueue } from './uploadQueue';

import { generateImageURL } from './blobStorage';

import { finishProcessingNCFileImage } from './manufacture';
import { getDocumentById } from './search';
import { updateUserPicture, getAuthInfo } from './user';
import { croppedMachineImage, rotatedMachineImage, finishProcessingMachineImage } from './machines';
import { addNewImageDocument, removeImageDocument, setImagesDocuments, setIsNoteAdding, updateExistedImagesDocuments } from './operatorPanel';
import { FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME } from '@components/operatorPanel/constants/operatorPanel.constants';
import { enqueue } from './retrieveQueue';

export const initState = {
  images: [],
  documentsObject: {},
  notes: {
    data: [],
    selectedNote: null,
  },
  loaded: false,
  queue: {},
  notification: [],
  context: {},
  rotatePull: [],
  documentCategories: {
    items: [],
    loaded: false,
  },
  preImageHandlingScreen: null,
  shouldAbortFetchingImages: false,
  imageStripFinished: false,
  updatingImageMetadataInProgress: false,
};

let sasIntervalID;

export const selectPreImageHandlingScreen = (state) => state.imageHandling.preImageHandlingScreen;

export const search = createAction('imageHandling/search');
export const updateNotification = createAction('imageHandling/updateNotification');
export const startSearch = createAction('imageHandling/startSearch');
export const setImages = createAction('imageHandling/setImages');
export const setDocuments = createAction('imageHandling/setDocuments');
export const setNotes = createAction('imageHandling/setNotes');
export const setSelectedNote = createAction('imageHandling/setSelectedNote');
export const clearState = createAction('imageHandling/clear');
export const clearStateKeepImages = createAction('imageHandling/clearStateKeepImages');
export const updateImages = createAction('imageHandling/favorite');
export const updateSasAction = createAction('imageHandling/updateSasAction');
export const addNewTempID = createAction('imageHandling/addNewTempID');
export const addActionToQueue = createAction('imageHandling/addActionToQueue');
export const connectBackendIDtoTempID = createAction('imageHandling/connectBackendIDtoTempID');
export const setConnectionId = createAction('imageHandling/setConnectionId');
const updateImageBlobAction = createAction('imageHandling/updateImageBlob');
export const updateSmallImageBlob = createAction('imageHandling/updateSmallImageBlob');
export const deleteFromRotatePull = createAction('imageHandling/deleteFromRotatePull');
export const addToRotatePull = createAction('imageHandling/addToRotatePull');
export const clear = createAction('clear');
export const setDocumentCategories = createAction('imageHandling/setDocumentCategories');
export const setPreImageHandlingScreenAction = createAction('imageHandling/setPreImageHandlingScreenAction');
export const setShouldAbortFetchingImagesAction = createAction('imageHandling/setShouldAbortFetchingImagesAction');
export const setImageStripFinished = createAction('imageHandling/setImageStripFinished');
export const setUpdatingImageMetadataInProgress = createAction('imageHandling/setUpdatingImageMetadataInProgress');
export const setImageContextAction = createAction('imageHandling/setImageContextAction');

const isTempID = (id) => {
  return String(id).includes('TEMPIMAGEID-');
};

export const loadImages = (images) => async (dispatch) => {
  for (const img of images) {
    if (img.cropped) {
      await dispatch(fetchImg(img, true, true));
      await dispatch(fetchImg(img, false, false));
    } else {
      await dispatch(fetchImg(img, false, false, true));
    }
  }
};

export const changePDFImage =
  (id, more = true) =>
  async (dispatch, getState) => {
    const {
      imageHandling: { images },
    } = getState();

    const page = more ? 1 : -1;

    let newImage = {};

    const newImages = images.map((i) => {
      if (i.id === id) {
        const newPage = i.page + page;
        const links = i.pagesList[newPage];
        newImage = {
          ...i,
          page: newPage,
          ...links,
        };

        dispatch(updateExistedImagesDocuments(newImage));
        return newImage;
      }
      return i;
    });

    dispatch(setImages(newImages));

    await dispatch(loadImages([newImage]));
  };

export const uploadImageNotififcation = (id) => async (dispatch, getState) => {
  const {
    imageHandling: { notification = [] },
  } = getState();
  const data = {
    id,
    type: 'upload',
    transparent: true,
    Message: () => <View />,
    Additional: () => (
      <View style={{ width: scale(20), position: 'absolute', right: 0 }}>
        <Svg icon="upload" width={scale(20)} height={scale(17)} color="Overlay/Light/Text Area On Picture" />
      </View>
    ),
  };
  dispatch(updateNotification([...notification, data]));
};

export const removeImageNotififcation = (id, item) => async (dispatch, getState) => {
  const {
    imageHandling: { notification = [] },
  } = getState();
  const data = {
    id,
    Message: ({ C, font }) => (
      <Text style={[{ color: C('Label / Light / Primary') }, font['Subhead 1']]}>This image has been deleted by another user.</Text>
    ),
    Additional: ({ scale }) => <Image source={item.source ? item.base64 : { uri: item.base64 }} style={{ height: scale(50), width: scale(50) }} />,
  };
  dispatch(updateNotification([...notification, data]));

  setTimeout(() => {
    dispatch(removeNotififcation(id));
  }, 5000);
};

export const newImageNotififcation = (id, item) => async (dispatch, getState) => {
  const {
    imageHandling: { notification = [] },
  } = getState();

  const data = {
    id,
    Message: ({ C, font }) => <Text style={[{ color: C('Label / Light / Primary') }, font['Subhead 1']]}>Added a new image.</Text>,
    Additional: ({ scale }) => <Image source={item.source ? item.base64 : { uri: item.base64 }} style={{ height: scale(50), width: scale(50) }} />,
  };
  dispatch(updateNotification([...notification, data]));

  setTimeout(() => {
    dispatch(removeNotififcation(id));
  }, 5000);
};

export const removeNotififcation = (id) => async (dispatch, getState) => {
  const {
    imageHandling: { notification = [] },
  } = getState();
  if (notification && notification.length) {
    const newNotification = notification.filter((i) => i.id !== id);
    dispatch(updateNotification(newNotification));
  }
};

export const startedProcessImageSignalR = (data) => async (dispatch, getState) => {
  const {
    imageHandling: { images },
  } = getState();
  const newImage = {
    id: data.imageHash,
    contentHash: data.imageHash,
    base64: null,
    width: 50,
    height: 50,
    widthOriginal: 50,
    heightOriginal: 50,
    activeImageWidth: 50,
    url: null,
    urlSmall: null,
    originUrl: null,
    isPreview: true,
    showImageUpload: true,
    pageCount: 1,
    page: 1,
    fileName: data.imageHash,
    local: true,
    customRotate: { 1: 0 },
    updatedDate: formatDate(moment().format('YYYY-MM-DD HH:mm')),
    tags: data.tags.map((el) => ({
      ...el,
      key: TAG_KEYS[el.scopeId],
      value: el.scopeReferenceId,
      name: el.scopeReferenceName,
      id: Math.random().toString(36).substring(7),
      selected: el.selected !== undefined ? el.selected : true,
      canBeFavorite: favoriteKey.includes(TAG_KEYS[el.scopeId]),
      favorite: el.isFavorite,
    })),
  };
  const newImages = [newImage, ...images];
  dispatch(setImages(newImages));
  dispatch(addNewImageDocument(newImage));
};

export const rotateImageSignalR =
  ({ documentId, cropSettings = {} }) =>
  async (dispatch, getState) => {
    const {
      imageHandling: { images },
    } = getState();

    const img = images.find((i) => i.id === documentId);

    if (!img) {
      return;
    }

    const rotationAngle = cropSettings?.rotationAngle;

    if (cropSettings && rotationAngle === img.angle) {
      let needPreview = false;
      const newImages = images.map((i) => {
        if (i.id === documentId) {
          needPreview = true;
          const newImage = { ...i, startedEditingImage: false };
          dispatch(updateExistedImagesDocuments(newImage));
          return newImage;
        }
        return i;
      });

      if (needPreview) {
        dispatch(setImages(newImages));
      }
      return;
    }
    if (img) {
      let additionalInfo = {};
      if (!img.startedEditingImage) {
        additionalInfo.wasEditEventWithoutStart = true;
      }

      additionalInfo.startedEditingImage = false;
      dispatch(addImageSignalR({ image: { ...img, documentId }, documentBlobId: img.id }, additionalInfo));
    }
  };

export const cropImageSignalR =
  ({ documentId }) =>
  async (dispatch, getState) => {
    const {
      imageHandling: { images },
    } = getState();

    const img = images.find((i) => i.id === documentId);

    if (img) {
      let additionalInfo = {};
      if (img.startedEditingImage) {
        additionalInfo.startedEditingImage = false;
      } else {
        additionalInfo.wasEditEventWithoutStart = true;
      }

      dispatch(addImageSignalR({ image: { ...img, documentId }, documentBlobId: img.id }, additionalInfo));
    }
  };

export const addImageSignalR =
  ({ image, documentBlobId }, additionalInfo = {}) =>
  async (dispatch, getState) => {
    const {
      imageHandling: { context, images, rotatePull, queue },
    } = getState();

    if (!context) {
      return;
    }

    setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);

    const scopeIdFromImage = image?.links?.[0]?.scopeId;
    const scopeReferenceIdFromImage = image?.links?.[0]?.scopeReferenceId;

    if (
      scopeIdFromImage != null &&
      scopeReferenceIdFromImage != null &&
      scopeReferenceIdFromImage === context?.scopeReferenceId &&
      scopeIdFromImage === context?.scopeId
    ) {
      const response = await dispatch(getDocumentById(image.documentId));

      if (response?.length) {
        const scopeName = SCOPE_ID_TO_TAG_NAME[scopeIdFromImage];
        if (scopeName) {
          dispatch(updateDocuments({ documents: response, scopeName, isDirect: true }));
        }
      }
    }

    const imageExist = images?.some((i) => i.id === image?.documentId || i.id === documentBlobId);

    if (!context.filters && !imageExist) {
      return;
    }

    const call = {
      method: (api) => api.imageHandling.getDocumentById,
      params: {
        documentId: image.documentId,
      },
      onSuccess: async (data) => {
        let exist = false;
        const image = await generateImages({ images: data }, (d) => dispatch(generateImageURL(d)));
        const img = { ...image[0], ...additionalInfo };
        let newImages = images.map((i) => {
          if (i.id === img.id || i.id === documentBlobId) {
            exist = true;
            return { ...i, ...img, tags: i.tags };
          }
          return i;
        });
        if (!exist) {
          newImages = [img, ...newImages];
          dispatch(newImageNotififcation(img.id, img));
        } else {
          dispatch(removeNotififcation(img.id));
        }

        // IMPORTANT: Do not change sequence of next function calls
        dispatch(setImages(newImages));
        dispatch(addNewImageDocument(img));
        dispatch(setIsNoteAdding(false));
        await dispatch(loadImages([img]));
      },
      onError: (error) => {},
    };

    if (rotatePull && rotatePull.includes(image.documentId)) {
      await dispatch(deleteFromRotatePull(image.documentId));
    } else {
      await dispatch(backendCall(call));
    }
  };

export const onUpdatedDocumentMetadata = (e) => async (dispatch) => {
  if (!e.isError) {
    dispatch(updateImageMetaSignalR(e));
  }
};

export const onRotatedImage = (e) => async (dispatch) => {
  if (!e.isError) {
    dispatch(rotateImageSignalR(e));
    dispatch(rotatedMachineImage(e));
  }
};

export const onCroppedImage = (e) => async (dispatch, getState) => {
  if (!e.isError) {
    dispatch(cropImageSignalR(e));
    dispatch(croppedMachineImage(e));
  }
};

export const onRemovedImage = (e) => async (dispatch) => {
  if (e.isError) {
    Alert({ title: i18n.t('Delete image error'), text: e.errorMessage });
  } else {
    dispatch(deleteImageSignalR(e.documentId));
  }
};

export const onProcessedImage = (e) => async (dispatch, getState) => {
  if (e.isError) {
    Alert({ title: i18n.t('Error on processed image'), text: e.errorMessage });
  } else {
    // Connect backend ID to temp ID if temp ID exists in queue
    const {
      imageHandling: { queue },
    } = getState();

    // Look for temp ID that matches the specific blob ID from this SignalR event
    const tempIdEntry = queue
      ? Object.entries(queue).find(([tempId, queueData]) => queueData?.backendId === e.documentBlobId && tempId.startsWith('TEMPIMAGEID-'))
      : null;

    if (tempIdEntry) {
      const [tempId] = tempIdEntry;
      // Update the existing backendId from blob ID to final document ID
      dispatch(connectBackendIDtoTempID({ id: e.image?.documentId, tempID: tempId }));
    }

    dispatch(finishProcessingMachineImage(e));
    dispatch(finishProcessingNCFileImage(e));
    setTimeout(() => {
      dispatch(addImageSignalR(e, { startedEditingImage: false }));
    }, 1000);

    if (e?.image?.tags?.find((i) => i?.scopeId === 7)) {
      dispatch(getAuthInfo());
    }
  }
};

export const onProcessedOtherDocument = (e) => async (dispatch, getState) => {
  // Connect backend ID to temp ID if temp ID exists in queue
  const {
    imageHandling: { queue },
  } = getState();
  const tempIdEntry = queue
    ? Object.entries(queue).find(([tempId, queueData]) => queueData?.backendId === null && tempId.startsWith('TEMPIMAGEID-'))
    : null;

  if (tempIdEntry) {
    const [tempId] = tempIdEntry;
    dispatch(connectBackendIDtoTempID({ id: e.documentId, tempID: tempId }));
  }

  setTimeout(() => {
    dispatch(addImageSignalR({ ...e, image: { links: e.documentLinks, documentId: e.documentId } }, { startedEditingImage: false }));
  }, 2000);
};

export const updateImageMetaSignalR = (data) => async (dispatch, getState) => {
  const {
    imageHandling: {
      images,
      notes: { data: notes, selectedNote },
    },
  } = getState();
  let exist = false;

  const documentLinks = data.links || data.tags || [];

  let newImages = images.map((i) => {
    let imageUpdated = false;
    let updatedImage = { ...i };

    // Handle documentLinks
    if (data.documentId === i.id) {
      exist = true;
      imageUpdated = true;

      // Update links array
      // updatedImage.links = documentLinks;
      if (!updatedImage?.links) {
        updatedImage.links = documentLinks;
      } else {
        documentLinks.forEach((link) => {
          const existedLinkIndex = updatedImage.links.findIndex(
            (l) => l.scopeId === link.scopeId && l.scopeReferenceId === link.scopeReferenceId && l.scopeReferenceName === link.scopeReferenceName
          );

          if (existedLinkIndex === -1 && link.isActivated) {
            updatedImage.links = [...updatedImage.links, link];
          }

          if (existedLinkIndex >= 0) {
            if (!link.isActivated) {
              updatedImage.links = updatedImage.links.filter((_, index) => index !== existedLinkIndex);
            }
          }
        });
      }

      // Also update tags for backward compatibility
      const newLinksObj = {};

      const newLinksKeys = documentLinks.map((t) => {
        const key = TAG_KEYS[t.scopeId];
        newLinksObj[key] = t;
        return key;
      });

      let tags = i.tags.map((t) => {
        const key = TAG_KEYS[t.scopeId];
        if (newLinksKeys.includes(key)) {
          return {
            ...t,
            selected: true,
            favorite: newLinksObj[key].isFavorite,
          };
        } else {
          return {
            ...t,
            selected: false,
            favorite: false,
          };
        }
      });

      updatedImage.tags = tags;
    }

    // Handle documentTags (documentType)
    // Only update documentType if documentTags is present in SignalR response
    // This prevents overwriting locally updated documentType when SignalR doesn't include it
    if (data.documentId === i.id && data.documentTags && data.documentTags.length > 0) {
      exist = true;
      imageUpdated = true;
      // Assuming first documentTag is the documentType
      updatedImage.documentType = data.documentTags[0];
    }

    dispatch(setUpdatingImageMetadataInProgress(false));
    dispatch(updateExistedImagesDocuments(imageUpdated ? updatedImage : i));
    return imageUpdated ? updatedImage : i;
  });

  if (exist) {
    dispatch(setImages(newImages));
  }

  const updatedNotes = notes.map((note) => {
    if (data.documentId !== note.documentId) {
      return note;
    }

    // Update links array
    const updatedNote = { ...note };

    if (!note?.links?.length) {
      updatedNote.links = documentLinks;
    } else {
      documentLinks.forEach((documentLink) => {
        const existedLinkIndex = note.links.findIndex(
          (noteLink) =>
            noteLink.scopeId === documentLink.scopeId &&
            noteLink.scopeReferenceId === documentLink.scopeReferenceId &&
            noteLink.scopeReferenceName === documentLink.scopeReferenceName
        );

        if (existedLinkIndex === -1 && documentLink.isActivated) {
          updatedNote.links = note.links.concat(documentLink);
        }

        if (existedLinkIndex >= 0 && !documentLink.isActivated) {
          updatedNote.links = note.links.filter((_, index) => index !== existedLinkIndex);
        }
      });
    }

    if (selectedNote && selectedNote.documentId === note.documentId) {
      dispatch(setSelectedNote(updatedNote));
    }

    return updatedNote;
  });

  dispatch(setNotes(updatedNotes));
};

export const deleteImageSignalR = (id) => async (dispatch, getState) => {
  const {
    imageHandling: { images },
    operatorPanel: { imagesDocuments },
    user: { userInfo = {} },
  } = getState();

  const img = images.find((i) => i.id === id);
  if (img) {
    const newImages = images.filter((i) => i.id !== id);
    dispatch(setImages(newImages));
    dispatch(removeImageNotififcation(id, img));
  }

  if (userInfo?.userPicture?.documentId === id) {
    dispatch(updateUserPicture(null));
  }
};

export const saveImageRotate = (data) => async (dispatch, getState) => {
  const call = {
    method: (api) => api.imageHandling.rotate,
    params: data,

    onSuccess: (d) => {
      dispatch(addToRotatePull(data.id));
    },
    onError: (error) => {},
  };
  await dispatch(backendCall(call));
};

export const rotateImage = (data) => async (dispatch) => {
  const call = {
    method: (api) => api.imageHandling.rotate,
    params: data,
  };

  try {
    const resp = await dispatch(
      eventCall({
        connectionName: 'appHub',
        eventName: 'OnRotatedImage',
        data: call,
        matchEventFunction: (e) => {
          return e.documentId == data.documentId;
        },
      })
    );

    trackEvent(TRACKING_EVENTS.DOCUMENT, {
      action: 'rotated',
      rotationDegrees: data.rotationAngle || 0,
    });
    return resp;
  } catch (e) {
    return { error: true };
  }
};

export const getByHash = (data) => async (dispatch) => {
  const call = {
    method: (api) => api.imageHandling.getByHash,
    params: data,
    onError: (error) => {},
  };
  return await dispatch(backendCall(call));
};

export const getDocument = (data) => async (dispatch) => {
  const call = {
    method: (api) => api.imageHandling.getDocument,
    params: data,
    onError: console.error,
  };

  const resp = await dispatch(backendCall(call));

  dispatch(setNotes(resp.filter((note) => note.extensionOriginal === 'rtf')));
};

let searchImagesSignal = null;
export const searchImages = (params) => async (dispatch, getState) => {
  const {
    imageHandling: { images },
  } = getState();

  // Cancel any ongoing search request
  if (searchImagesSignal && !searchImagesSignal.signal.aborted) {
    searchImagesSignal.abort();
  }

  // Create new signal for this request
  searchImagesSignal = new AbortController();
  const signal = searchImagesSignal.signal;

  // Check if signal is already aborted
  if (signal.aborted) {
    return;
  }

  dispatch(startSearch(params));

  if (sasIntervalID) {
    clearInterval(sasIntervalID);
  }

  const onSuccess = async (data) => {
    // Check if request was aborted before processing
    if (signal.aborted) {
      return;
    }

    let allImages = data.documents;

    if (data.stateImages && data.stateImages.length === 1) {
      const img = data.stateImages[0];
      let fullImg = null;
      const filtered = allImages.filter((i) => {
        if (i.documentId === img.id) {
          fullImg = i;
          return false;
        }
        return true;
      });
      if (fullImg) {
        allImages = [fullImg, ...filtered];
      }
    }
    data.images = allImages;
    const images = await generateImages(data, (d) => dispatch(generateImageURL(d)));
    data.generateImages = images;
    dispatch(search(data));

    await dispatch(loadImages(images));

    return data;
  };

  if (params?.filters?.DocumentId?.value) {
    const image = await dispatch(getDocumentById(params.filters.DocumentId.value, signal));
    return await onSuccess({ documents: image || [], stateImages: images });
  }

  // Convert old filter structure to new typed request format
  let requestBody = null;

  if (params?.filters) {
    if (params.filters.Part) {
      requestBody = {
        $type: 'filterPartDocumentsRequest',
        partIds: [parseInt(params.filters.Part.value)],
      };
    } else if (params.filters.Clamping) {
      requestBody = {
        $type: 'filterNcFileDocumentsRequest',
        ncFileIds: [parseInt(params.filters.Clamping.value)],
      };
    } else if (params.filters.ProductionOrder) {
      requestBody = {
        $type: 'filterProductionOrderDocumentsRequest',
        productionOrderIds: [parseInt(params.filters.ProductionOrder.value)],
      };
    } else if (params.filters.Operation) {
      requestBody = {
        $type: 'filterProductionOperationDocumentsRequest',
        productionOperationIds: [parseInt(params.filters.Operation.value)],
      };
    } else if (params.filters.Machine && !params.filters.Part && !params.filters.Clamping && !params.filters.ProductionOrder) {
      // Machine filter alone - no direct mapping in new API, fallback to old structure
      requestBody = null;
    }
  }

  // Helper function to make the API call with retry logic
  // Fix for retrieveReferencedDocuments API failing on first request in NC File
  const makeAPICallWithRetry = async (retryCount = 0) => {
    const call = {
      method: (api) => api.imageHandling.searchDocuments,
      signal,
      params: requestBody || {
        // Fallback to old structure if no matching filter found
        pagination: {
          pageNumber: 1,
          pageSize: 200,
          includeCount: true,
        },
        orderBy: '[{ "FieldName": "CreatedOn", "SortOrder": "Desc" }]',
        ...params,
      },
      onSuccess,
      onError: async (error) => {
        // Don't retry if request was aborted
        if (error.name === 'AbortError' || signal.aborted) {
          return;
        }

        // Retry logic: if this is the first call and we have a Clamping filter (NC File), retry once
        // This handles the case where the first request to retrieveReferencedDocuments fails
        // but subsequent requests work correctly
        if (retryCount === 0 && params?.filters?.Clamping && requestBody) {
          // Add a small delay before retry
          await new Promise((resolve) => setTimeout(resolve, 500));
          return makeAPICallWithRetry(1);
        }

        // If still failing after retry and we used the new typed format, try fallback to old format
        // This provides a fallback mechanism to the old request structure
        if (retryCount === 1 && requestBody && params?.filters?.Clamping) {
          // Set requestBody to null to force old format
          requestBody = null;
          await new Promise((resolve) => setTimeout(resolve, 300));
          return makeAPICallWithRetry(2);
        }

        dispatch(search({}));
      },
    };

    return await dispatch(backendCall(call));
  };

  return await makeAPICallWithRetry();
};

export const fetchImg = (img, isCropped, useCropped, useOriginal) => async (dispatch) => {
  if (img.isDocument) {
    return;
  }
  try {
    const success = (file) => {
      if (useCropped) {
        return dispatch(
          updateImageBlob({
            id: img.id,
            base64: file,
          })
        );
      }
      Image.getSize(file, (width, height) => {
        const cropSettings = img.cropSettings || [];
        let crop = {};
        const page = img.page || 0;
        let c = cropSettings.find((s) => s.pageNumber === page);
        if (c) {
          const angle = c.rotationAngle;
          if (angle == 0 || angle == 180) {
            crop = {
              width: Math.round(width * (c.lowerRightXPosition - c.upperLeftXPosition)),
              height: Math.round(height * (c.lowerRightYPosition - c.upperLeftYPosition)),
              x: Math.round(c.upperLeftXPosition * width),
              y: Math.round(c.upperLeftYPosition * height),
            };
          } else {
            crop = {
              width: Math.round(height * (c.lowerRightXPosition - c.upperLeftXPosition)),
              height: Math.round(width * (c.lowerRightYPosition - c.upperLeftYPosition)),
              x: Math.round(c.upperLeftXPosition * height),
              y: Math.round(c.upperLeftYPosition * width),
            };
          }
          crop.angle = angle;
          crop.activeImageWidth = limitMinimumSize((scale(smallImageHeight) * crop.width) / crop.height);
        }
        const data = {
          id: img.id,
          size3x: { width, height },
          showPDFUploading: false,
          isPreview: false,
          ...crop,
        };

        if (isCropped) {
          data.base64 = file;
        } else {
          data.base64Original = file;
          if (useOriginal) {
            data.base64 = file;
          }
        }

        if (img.urlSmall) {
          data.urlSmall = img.urlSmall + `&dummy=${Math.round(Math.random() * 1000)}`;
        }

        dispatch(updateImageBlob(data));
      });
    };
    const url = isCropped ? img.url : img.originUrl3x;

    const dowImg = await fetch(url, { ignoreInterceptor: 'true' });

    if (!dowImg.ok) {
      throw new Error(`Fetch failed: ${dowImg.status} ${dowImg.statusText}`);
    }

    const blob = await dowImg.blob();

    const fileReaderInstance = new FileReader();
    fileReaderInstance.readAsDataURL(blob);
    fileReaderInstance.onload = () => {
      const base64data = fileReaderInstance.result;

      if (isAndroid) {
        const dirs = ReactNativeBlobUtil.fs.dirs.CacheDir;
        const path = dirs + `/${img.id}_${Math.random() * 10000}.png`;
        ReactNativeBlobUtil.fs.writeFile(path, base64data.replace('data:application/octet-stream;base64,', ''), 'base64').then(() => {
          const file = `file://${path}`;
          success(file);
        });
      } else {
        success(base64data);
      }
    };

    fileReaderInstance.onerror = (error) => {};
  } catch (e) {
    dispatch(updateImageBlob({ id: img.id, base64: null, size3x: {} }));
  }
};

export const updateImageMetadata = (d) => async (dispatch, getState) => {
  const {
    imageHandling: { queue },
  } = getState();
  let data = Object.assign({}, d);

  setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);

  const hash = data.documentId;

  if (isTempID(hash)) {
    if (queue[hash] && queue[hash].backendId) {
      data.documentId = queue[hash].backendId;
    } else {
      return dispatch(addActionToQueue({ tempID: hash, action: { data, type: 'updateImageMetadata' } }));
    }
  }

  return new Promise((resolve, reject) => {
    const call = {
      method: (api) => api.imageHandling.updateImageMetadata,
      params: data,

      onSuccess: (r) => {
        resolve(r);
      },
      onError: (error) => {
        Alert({ title: 'Error update image metadata. Please, try again.' });
        reject(error);
      },
    };
    dispatch(backendCall(call));
  });
};

export const cropImage = (d) => async (dispatch, getState) => {
  const {
    imageHandling: { queue },
  } = getState();
  let data = Object.assign({}, d);
  const hash = data.documentId;

  if (isTempID(hash)) {
    if (queue[hash] && queue[hash].backendId) {
      data.documentId = queue[hash].backendId;
    } else {
      return dispatch(addActionToQueue({ tempID: hash, action: { data, type: 'crop' } }));
    }
  }

  const call = {
    method: (api) => api.imageHandling.cropImage,
    params: data,

    onSuccess: (r) => {
      trackEvent(TRACKING_EVENTS.DOCUMENT, { action: 'cropped' });
    },
    onError: (error) => {
      Alert({ title: 'Error during crop image. Please, try again.' });
    },
  };
  await dispatch(backendCall(call));
};

export const runQueueActions = (tempID, id) => async (dispatch, getState) => {
  const {
    imageHandling: { queue, images },
  } = getState();

  if (queue && queue[tempID] && queue[tempID].actions.length) {
    const actions = queue[tempID].actions;
    for (const action of actions) {
      switch (action.type) {
        case 'crop':
          await dispatch(cropImage(action.data));
          break;
        case 'updateImageMetadata':
          await dispatch(updateImageMetadata(action.data));
          break;
      }
    }
  }

  const newImages = images.map((i) => {
    if (i.id === tempID) {
      const updatedImage = {
        ...i,
        id,
      };
      dispatch(updateExistedImagesDocuments(updatedImage));
      return updatedImage;
    }
    return i;
  });
  dispatch(setImages(newImages));
};

const addImageWithBase = (imgData, enableStartQueue, base64) => async (dispatch) => {
  try {
    imgData.img.base64 = base64;
    await dispatch(updateUploadQueue(imgData));
    if (enableStartQueue) {
      await dispatch(startQueue());
    }
  } catch (e) {}
};

export const addImage =
  ({ photo, tags, documentType, tempID, isPDF, isDocument, imageCropSettings, rawContent }, enableStartQueue = true) =>
  async (dispatch) => {
    const imageName = `image-${tempID}`;
    const imgData = {
      id: imageName,
      status: 'pending',
      name: photo.name,
      time: moment().format('YYYY-MM-DD HH:mm:ss'),
      img: {
        tags,
        documentType,
        photo,
        isPDF,
        isDocument,
        tempID,
        imageCropSettings,
      },
    };

    if (isWeb) {
      const base64 = await toBase64(photo.file ? photo.file : photo);
      dispatch(addImageWithBase(imgData, enableStartQueue, base64));
    } else {
      const base64 = rawContent ? ReactNativeBlobUtil.base64.encode(rawContent) : await RNFS.readFile(photo.uri, 'base64');
      dispatch(addImageWithBase(imgData, enableStartQueue, base64));
    }
  };

export const getBrowser = () => {
  let sBrowser;
  let sUsrAg = window.navigator.userAgent;

  if (sUsrAg.indexOf('Firefox') > -1) {
    sBrowser = 'Mozilla Firefox';
  } else if (sUsrAg.indexOf('Opera') > -1) {
    sBrowser = 'Opera';
  } else if (sUsrAg.indexOf('Trident') > -1) {
    sBrowser = 'Microsoft Internet Explorer';
  } else if (sUsrAg.indexOf('Edge') > -1) {
    sBrowser = 'Microsoft Edge';
  } else if (sUsrAg.indexOf('Chrome') > -1) {
    sBrowser = 'Google Chrome';
  } else if (sUsrAg.indexOf('Safari') > -1) {
    sBrowser = 'Apple Safari';
  } else {
    sBrowser = 'Unknown Browser';
  }

  return sBrowser;
};

const updateFileInBlobStorage = async ({ blobBaseUrl, sasToken, file, content }) => {
  const fullBlobUrl = `${blobBaseUrl}?${sasToken}`;

  if (isWeb) {
    const blobClient = new BlockBlobClient(fullBlobUrl);
    const options = { blobHTTPHeaders: { blobContentType: file.type } };

    await blobClient.uploadData(file, options);
  } else {
    const base64Content = ReactNativeBlobUtil.base64.encode(content);

    await ReactNativeBlobUtil.fetch(
      'PUT',
      fullBlobUrl,
      {
        'x-ms-blob-type': 'BlockBlob',
        'content-type': 'application/octet-stream',
        'x-ms-blob-content-type': file.type,
        ignoreInterceptor: 'true',
      },
      base64Content
    );
  }
};

const uploadFileToBlob = async ({ file, blobBaseUrl, sasToken, blobContainer, folder, fileName, base64 }) => {
  const blobService = new BlobServiceClient(`${blobBaseUrl}/?${sasToken}`);
  const containerClient = blobService.getContainerClient(`${blobContainer}`);
  if (isWeb) {
    await createBlobInContainer({ containerClient, file, fileName, folder });
  } else {
    await ReactNativeBlobUtil.fetch(
      'PUT',
      `${blobBaseUrl}/${blobContainer}/${folder.replace('/', '')}/${fileName}?${sasToken}`,
      {
        'x-ms-blob-type': 'BlockBlob',
        'content-type': 'application/octet-stream',
        'x-ms-blob-content-type': file.type,
        ignoreInterceptor: 'true',
      },
      base64 ?? ReactNativeBlobUtil.wrap(file.uri)
    );
  }
};

const createBlobInContainer = async ({ containerClient, file, fileName, folder }) => {
  const blobClient = containerClient.getBlockBlobClient(`${folder.replace('/', '')}/${fileName}`);
  const options = { blobHTTPHeaders: { blobContentType: file.type } };
  await blobClient.uploadData(file, options);
};

export const uploadImage =
  ({ photo, tags, imageCropSettings, tempID, isPDF, documentType, name, rawContent }) =>
  async (dispatch) => {
    try {
      dispatch(addNewTempID(tempID));

      const file = photo.file ? photo.file : photo;

      const originalName = file.name === 'undefined' ? name : file.name;

      const d = await dispatch(
        backendCall({
          method: (api) => api.imageHandling.requestForDocumentUpload,
          params: {
            $type: 'new',
            fileName: originalName,
            sourceType: 'App',
          },
        })
      );

      const { blobAccessInfo, documentBlobId, documentBlobName } = d;
      const fileName = documentBlobName;

      await uploadFileToBlob({
        file,
        fileName,
        blobBaseUrl: blobAccessInfo.blobBaseUrl,
        sasToken: blobAccessInfo.sasToken,
        blobContainer: blobAccessInfo.blobContainer,
        folder: blobAccessInfo.folders?.find((f) => f.folderType === 'Image')?.folderName || 'file/',
        base64: rawContent ? ReactNativeBlobUtil.base64.encode(rawContent) : undefined,
      });

      const devName = await DeviceInfo.getDeviceName();
      const model = await DeviceInfo.getModel();
      const sourceName = isWeb ? getBrowser() : `${model} / ${devName}`;

      dispatch(uploadImageNotififcation(tempID));

      const shouldAddCropInfo = !(
        Number(imageCropSettings?.lowerRightXPosition) === 1 &&
        Number(imageCropSettings?.lowerRightYPosition) === 1 &&
        Number(imageCropSettings?.upperLeftXPosition) === 0 &&
        Number(imageCropSettings?.upperLeftYPosition) === 0 &&
        Number(imageCropSettings?.rotationAngle) === 0
      );

      const call = {
        method: (api) => api.imageHandling.processDocument,
        params: {
          documentBlobId,
          documentType: documentType === 'NcFile' || documentType === 'Note' ? documentType : 'ImageOrOther',
          documentTags: documentType ? [documentType] : null,
          fileName: originalName,
          sourceName,
          documentLinks: tags.map((t) => ({ ...t, scopeReferenceId: t.scopeReferenceId, scopeId: Number(t.scopeId) })),
          ...(shouldAddCropInfo ? { imageCropSettings } : {}),
          sourceType: isWeb ? imageSource.AppUploadFromFileSystem : imageSource.AppCameraOrPhotoLibrary,
        },
        onSuccess: async () => {
          setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);
          dispatch(connectBackendIDtoTempID({ id: documentBlobId, tempID }));
          dispatch(runQueueActions(tempID, documentBlobId));
          dispatch(removeNotififcation(tempID));
          trackEvent(TRACKING_EVENTS.DOCUMENT, {
            action: 'added',
            documentType: documentType || 'ImageOrOther',
            fileSizeBytes: 0,
            result: 'success',
          });
          if (isPDF) {
            dispatch(uploadImageNotififcation(documentBlobId));
          }
        },
        onError: (error) => {
          trackEvent(TRACKING_EVENTS.DOCUMENT, {
            action: 'added',
            documentType: documentType || 'ImageOrOther',
            // fileSizeBytes: 0,
            result: 'failed',
          });
          dispatch(removeNotififcation(tempID));
        },
      };
      await dispatch(backendCall(call));
      return { documentBlobId };
    } catch (e) {}
  };

export const updateImage =
  ({ contentHash, fileName, file, textContent }) =>
  async (dispatch) => {
    const response = await dispatch(
      backendCall({
        method: (api) => api.imageHandling.requestForDocumentUploadV2,
        params: {
          $type: 'existing',
          contentHash: contentHash,
          fileName: fileName,
        },
      })
    );

    const blobBaseUrl = response.blobAccessInfo.blobUrl;
    const sasToken = response.blobAccessInfo.sasToken;

    await updateFileInBlobStorage({ blobBaseUrl, sasToken, file, content: textContent });
  };

export const deleteImage = (id) => async (dispatch, getState) => {
  const call = {
    method: (api) => api.imageHandling.removeImage,
    params: { documentId: id },
    onSuccess: async () => {
      setExpirationTimeAsTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);
    },
    onError: (error) => {
      Alert({ title: 'Error during deleting the image. Please, try again.' });
    },
  };
  await dispatch(backendCall(call));
};

export const deleteDocument = (id) => async (dispatch) => {
  const call = {
    method: (api) => api.imageHandling.removeDocument,
    params: { documentId: id },
    onSuccess: () => {},
    onError: (error) => {
      Alert({ title: 'Error during deleting the document. Please, try again.' });
    },
  };
  await dispatch(backendCall(call));
};

export const fetchDocumentCategories = () => async (dispatch, getState) => {
  const call = {
    method: (api) => api.imageHandling.getDocumentCategories,
    onSuccess: async (data) => {
      // Handle OData format and map to expected structure
      const categories = data.value || data;
      const mappedCategories = categories.map((item) => ({
        name: item.name || item.Name,
        value: item.id || item.Id || item.value || item.name,
        title: item.title || item.Title || '',
      }));

      dispatch(setDocumentCategories(mappedCategories));
    },
    onError: (error) => {},
  };
  await dispatch(backendCall(call));
};

export const setPreImageHandlingScreen = (screen) => (dispatch) => {
  dispatch(setPreImageHandlingScreenAction(screen));
};

export const setShouldAbortFetchingImages = (value) => (dispatch) => {
  dispatch(setShouldAbortFetchingImagesAction(value));
};

export const checkIsContextForImagePropDrillingTheSame = (context, savedContext) => (dispatch, getState) => {
  // For operator panel requests, always use the passed savedContext to avoid cross-contamination
  // For main store requests, fall back to global state for backward compatibility
  const state = getState();
  const { context: savedContextInReducer, cleared } = state.imageHandling;

  const imageSearchingContext = savedContext ?? savedContextInReducer;

  if (!imageSearchingContext || imageSearchingContext.scopeId == null || imageSearchingContext.scopeReferenceId == null) {
    return false;
  }

  if (context.scopeId && imageSearchingContext.scopeId !== context.scopeId) {
    return false;
  }

  if (context.scopeReferenceId && imageSearchingContext.scopeReferenceId !== context.scopeReferenceId) {
    return false;
  }

  if (imageSearchingContext.retrieveAllDocuments !== context.retrieveAllDocuments) {
    return false;
  }

  if (imageSearchingContext.forceRetrieveFromSource !== context.forceRetrieveFromSource) {
    return false;
  }

  // Check if current screen is different - different screens should have different contexts
  // This prevents cached images from one screen being used in another screen
  if (context.currentScreen && imageSearchingContext.currentScreen && imageSearchingContext.currentScreen !== context.currentScreen) {
    return false;
  }

  // If cleared flag is true, always force reload - this ensures we get fresh images
  // This handles the case where user navigates from part -> no part -> same part
  // For operator panel context (when savedContext is passed), ignore cleared flag
  // to prevent blocking image loading on initial mount
  if (cleared && !savedContext) {
    return false;
  }

  return true;
};

export const retrieveReferencedDocumentsFn = (params) => async (dispatch, getState) => {
  const {
    documentKey,
    ids,
    abortSignal,
    storeMode = DOCUMENTS_STORE_MODE.DIRECT_INDIRECT_LINKING,
    retrieveAllDocuments = true,
    onRetrieveFinished,
    impactMainStore = true,
  } = params || {};


  const isForceRetrieveFromSourceExpired = await isExpiredTimestamp(FORCE_RETRIEVE_FROM_SOURCE_KEY_EXPIRATION_TIME);

  const forceRetrieveFromSource = !isForceRetrieveFromSourceExpired;

  const scopeId = DOCUMENT_TYPE_TAG_KEY_MAP?.[documentKey];
  const scopeReferenceId = ids?.[0];

  const currentState = getState();
  const currentImages = currentState.imageHandling?.images;

  if (scopeId == null || scopeReferenceId == null) {
    dispatch(setImageStripFinished(true));
    return;
  }

  // Only check context for main store to maintain backward compatibility
  // Operator panel needs to run independently
  const contextMatches = dispatch(
    checkIsContextForImagePropDrillingTheSame({ scopeId, scopeReferenceId, retrieveAllDocuments, forceRetrieveFromSource })
  );

  // Check if documents actually exist in the store
  const { documentsObject } = getState().imageHandling;
  const hasDocuments = documentsObject && Object.values(documentsObject).some((docs) => Array.isArray(docs) && docs.length > 0);

  // Only skip fetching if context matches AND we actually have documents
  if (impactMainStore && contextMatches && hasDocuments) {
    dispatch(setImageStripFinished(true));
    return;
  }

  if (impactMainStore) {
    dispatch(clearState());
    // Explicitly reset imageStripFinished when clearing state for new document
    dispatch(setImageStripFinished(false));
  } else {
    dispatch(clearStateKeepImages());
    // Also reset for non-main store to ensure proper loading state
    dispatch(setImageStripFinished(false));
  }

  if (!documentKey || !ids || ids.length === 0) {
    dispatch(setImageStripFinished(true));
    return;
  }
  let config = DOCUMENT_HIERARCHY_CONFIG[documentKey];
  if (!config) return;

  let initialPriority = config.priority;
  if (typeof initialPriority !== 'number') {
    dispatch(setImageStripFinished(true));
    return;
  }

  // Create execution context for this specific request
  const executionContext = { scopeId, scopeReferenceId, retrieveAllDocuments, forceRetrieveFromSource };

  // Only set global context for main store to maintain backward compatibility
  if (impactMainStore) {
    dispatch(startSearch(executionContext));
  }

  const idsConfig = Object.entries(DOCUMENT_HIERARCHY_CONFIG).reduce((acc, [key, c]) => {
    if (!acc[key]) {
      acc[key] = c.priority === initialPriority ? ids : []; // We need to handle exception cases, where we can look in higher priority
    }
    return acc;
  }, {});

  const data = {};
  const visitedConfigsWithExceptions = [];
  const visitedConfigs = [];
  let allAccumulatedImages = []; // Accumulate images across all iterations

  // iterate for image prop drilling
  while (initialPriority > 0) {
    // if exception config was visited we should skip it to not trap into infinite loop
    if (visitedConfigsWithExceptions.includes(config.key) || visitedConfigs.includes(config.key)) {
      initialPriority--;
      if (initialPriority === 0) break;

      let newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
      // If no config found at this priority, continue looking for lower priorities
      while (!newConfigItem && initialPriority > 1) {
        initialPriority--;
        newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
      }
      if (!newConfigItem) {
        break;
      }

      config = newConfigItem;
      continue;
    }

    // check if we earlier visit current config. If not, add it to visited
    if (!visitedConfigs.includes(config.key)) {
      visitedConfigs.push(config.key);
    }

    // abort if needed - pass execution context for operator panel to avoid global state contamination
    const shouldAbortFetchingImages = !dispatch(
      checkIsContextForImagePropDrillingTheSame(
        { scopeId, scopeReferenceId, forceRetrieveFromSource, retrieveAllDocuments },
        impactMainStore ? undefined : executionContext // Pass context for operator panel
      )
    );

    if (shouldAbortFetchingImages || !config.scopeKey || !config.key || !idsConfig[config.key]) {
      break;
    }

    // if config doesn't have any ids to retrieve
    if (!idsConfig[config.key]?.length) {
      let newConfigItem;

      // update initialPriority and config with default variant or follow exception instructions
      if (!!config?.exceptionLinkToConfigItem) {
        // add config into visited exception configs array
        visitedConfigsWithExceptions.push(config.key);

        newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.key === config?.exceptionLinkToConfigItem);
        if (!newConfigItem) {
          break;
        }
        initialPriority = newConfigItem.priority;
      } else {
        initialPriority--;
        if (initialPriority === 0) {
          break;
        }

        newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
        // If no config found at this priority, continue looking for lower priorities
        while (!newConfigItem && initialPriority > 1) {
          initialPriority--;
          newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
        }
        if (!newConfigItem) {
          break;
        }
      }

      config = newConfigItem;
      continue;
    }

    const call = {
      method: (api) => api.imageHandling.retrieveReferencedDocuments,
      signal: abortSignal,
      params: {
        $type: config.requestType,
        [config.scopeKey]: idsConfig[config.key],
        retrieveAllDocuments,
        forceRetrieveFromSource,
      },
    };

    // backendCall
    const response = await dispatch(backendCall(call));

    // Validate response matches request context to prevent cross-contamination
    const expectedType = config.requestType;
    const actualType = response?.['$type'];

    // Check if response type matches request type (allowing Request->Response suffix conversion)
    const expectedResponseType = expectedType?.replace('Request', 'Response');

    if (actualType && actualType !== expectedResponseType) {
      // Skip processing this contaminated response
      initialPriority--;
      if (initialPriority === 0) break;

      const newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
      if (!newConfigItem) break;

      config = newConfigItem;
      continue;
    }

    // Remove the suspicious response detection as it's too aggressive and blocking valid responses
    // The queue isolation and response type validation should be sufficient

    // update the ids of related elements

    for (let relatedIdsKey of Object.values(DOCUMENT_RELATED_IDS_KEYS)) {
      if (response?.[relatedIdsKey] && Array.isArray(response[relatedIdsKey]) && response[relatedIdsKey]?.length > 0) {
        const entityKey = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.responseKey === relatedIdsKey)?.key;
        if (!entityKey) {
          continue;
        }
        idsConfig[entityKey] = [...new Set([...idsConfig[entityKey], ...response[relatedIdsKey]])];
      }
    }

    const uniqueDocuments = dispatch(filterDocumentsByContentHash(response?.documents, { impactMainStore, documentKey }));

    data.images = uniqueDocuments;
    data.documents = uniqueDocuments;

    const images = await generateImages(data, (d) => dispatch(generateImageURL(d)));

    // Accumulate images across all iterations instead of overwriting
    allAccumulatedImages = [...allAccumulatedImages, ...images];

    // Sort the accumulated images
    data.generateImages = sortDocumentsByField({ documents: allAccumulatedImages, field: 'createdAt' });

    if (impactMainStore) {
      dispatch(search(data));

      // Parse and store documents with document scope name (depends on store mode)
      const { scopeName, key } = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.responseType === response?.['$type']) ?? {};

      if (scopeName && key) {
        const isDirect = key === documentKey;
        dispatch(updateDocuments({ documents: uniqueDocuments, scopeName, isDirect, storeMode }));
      }
    } else {
      // For operator panel context, update the operator panel store
      // But only if we have meaningful data to avoid corruption
      if (allAccumulatedImages.length > 0) {
        const { setImagesDocuments } = await import('./operatorPanel');
        dispatch(setImagesDocuments(allAccumulatedImages));
      }
    }

    await dispatch(loadImages(images));

    // update values for next iteration with default variant or follow exception instructions

    let newConfigItem;

    if (!!config?.exceptionLinkToConfigItem) {
      // add config into visited exception configs array
      visitedConfigsWithExceptions.push(config.key);

      newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.key === config?.exceptionLinkToConfigItem);
      if (!newConfigItem) {
        break;
      }
      initialPriority = newConfigItem.priority;
    } else {
      initialPriority--;
      if (initialPriority === 0) {
        break;
      }

      newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
      // If no config found at this priority, continue looking for lower priorities
      while (!newConfigItem && initialPriority > 1) {
        initialPriority--;
        newConfigItem = Object.values(DOCUMENT_HIERARCHY_CONFIG).find((c) => c.priority === initialPriority);
      }
      if (!newConfigItem) {
        break;
      }
    }

    config = newConfigItem;
  }

  dispatch(setImageStripFinished(true));

  if (onRetrieveFinished) {
    onRetrieveFinished(data.generateImages);
  } else {
  }

  return data;
};

export const retrieveReferencedDocuments = (params) => async (dispatch) => {
  return dispatch(enqueue(retrieveReferencedDocumentsFn, params));
};

export const updateDocuments =
  ({ documents, scopeName: _scopeName, isDirect, storeMode = DOCUMENTS_STORE_MODE.DIRECT_INDIRECT_LINKING, appendToStart = true }) =>
  (dispatch, getState) => {
    const { documentsObject: documentsFromReducer } = getState().imageHandling;
    let updatedDocumentsObject;

    const scopeName = !isDirect && storeMode === DOCUMENTS_STORE_MODE.DIRECT_INDIRECT_LINKING ? INDIRECT_LINKING_TEXT : _scopeName;

    // Always use main store context when updating documentsObject, regardless of isDirect
    // This ensures we don't filter against operator panel when storing in main store
    const uniqueDocuments = dispatch(filterDocumentsByContentHash(documents, { impactMainStore: true, skipImageArrayCheck: true }));

    if (!documentsFromReducer[scopeName]) {
      updatedDocumentsObject = { ...documentsFromReducer, [scopeName]: uniqueDocuments };
    } else {
      const updatedArray = appendToStart
        ? [...uniqueDocuments, ...documentsFromReducer[scopeName]]
        : [...documentsFromReducer[scopeName], ...uniqueDocuments];

      const sortedUpdatedArray = sortDocumentsByField({ documents: updatedArray });

      updatedDocumentsObject = {
        ...documentsFromReducer,
        [scopeName]: sortedUpdatedArray,
      };
    }

    dispatch(setDocuments(updatedDocumentsObject));
  };

const filterDocumentsByContentHash =
  (documents, contextInfo = {}) =>
  (_, getState) => {
    const { documentsObject: documentsFromReducer, images: mainStoreImages } = getState().imageHandling;
    const { imagesDocuments: operatorPanelImages } = getState().operatorPanel;
    const { impactMainStore = false, documentKey = null, skipImageArrayCheck = false } = contextInfo;

    let existingHashes = new Set();

    if (impactMainStore) {
      // When updating documentsObject, don't filter against images array to prevent double-filtering
      if (skipImageArrayCheck) {
        // Only filter against existing documentsObject to prevent duplicates within documentsObject
        const allExistingDocs = Object.values(documentsFromReducer).flat();
        existingHashes = new Set(allExistingDocs.map((d) => d?.contentHash).filter(Boolean));
      } else {
        // For main store updates (like ProductionOperation), only check against main store images
        // Don't filter against operatorPanel contexts to prevent cross-context filtering
        existingHashes = new Set(mainStoreImages.map((d) => d?.contentHash).filter(Boolean));
      }
    } else {
      // For operatorPanel contexts (like Batch), filter against operator panel images
      // This ensures complete isolation between the two stores
      existingHashes = new Set(operatorPanelImages.map((d) => d?.contentHash).filter(Boolean));
    }

    const filtered = documents?.filter((d) => d?.contentHash && !existingHashes.has(d.contentHash)) || [];

    return filtered;
  };

const sortDocumentsByField = ({ documents, field = 'createdOn', direction = 'desc' }) => {
  return [...documents].sort((a, b) => {
    // Primary sort by the specified field (usually createdOn)
    const primaryCompare = direction === 'desc' ? Date.parse(b[field]) - Date.parse(a[field]) : Date.parse(a[field]) - Date.parse(b[field]);

    // If dates are equal, use documentId as a tie-breaker for consistent ordering
    if (primaryCompare === 0 || isNaN(primaryCompare)) {
      // Sort by documentId (or id) to ensure deterministic ordering
      const aId = a.documentId || a.id || 0;
      const bId = b.documentId || b.id || 0;
      return direction === 'desc' ? bId - aId : aId - bId;
    }

    return primaryCompare;
  });
};

export const handleToggleFavoriteLink =
  ({ scopeId, scopeReferenceId, img }) =>
  async (dispatch, getState) => {
    if (!img || !img.links) return;

    const prevLinks = img.links;

    // Build updated links array with toggled favorite state (only within same link)
    const newLinks = prevLinks.map((l) => {
      if (l.scopeId === scopeId && l.scopeReferenceId === scopeReferenceId) {
        // Toggle current link
        return { ...l, isFavorite: !l.isFavorite };
      }
      return l;
    });

    const { images } = getState()?.imageHandling;

    // Update local images state immediately for UX
    const updatedImages = images.map((image) => (image.id === img.id ? { ...image, links: newLinks } : image));
    dispatch(setImages(updatedImages));

    // Build map prev favorite
    const prevFavMap = {};
    prevLinks.forEach((pl) => {
      prevFavMap[`${pl.scopeId}_${pl.scopeReferenceId}`] = pl.isFavorite;
    });

    const backendData = {
      documentId: img.id,
      documentLinks: newLinks.map((l) => ({
        scopeId: l.scopeId,
        scopeReferenceId: l.scopeReferenceId,
        scopeReferenceName: l.scopeReferenceName,
        isFavorite: l.isFavorite,
        isActivated: true, // link exists means active
        isFavoriteChanged: prevFavMap[`${l.scopeId}_${l.scopeReferenceId}`] !== l.isFavorite,
      })),
      documentTags: img?.documentType ? [img.documentType] : null,
    };

    dispatch(updateImageMetadata(backendData));
  };

export const handleRemoveAttachment =
  ({ img, attachmentToRemove }) =>
  async (dispatch) => {
    dispatch(setUpdatingImageMetadataInProgress(true));

    if (attachmentToRemove && img && img.links) {
      // Remove attachment from local state
      const newLinks = img.links.filter(
        (l) => !(l.scopeId === attachmentToRemove.scopeId && l.scopeReferenceId === attachmentToRemove.scopeReferenceId)
      );

      // Prepare backend data - send all links with their current state
      const backendData = {
        documentId: img.id,
        documentLinks: [
          // Keep existing links as activated
          ...newLinks.map((l) => ({
            scopeId: l.scopeId,
            scopeReferenceId: l.scopeReferenceId,
            scopeReferenceName: l.scopeReferenceName,
            isFavorite: l.isFavorite,
            isActivated: true,
            isFavoriteChanged: false,
          })),
          // Mark the removed link as deactivated
          {
            ...attachmentToRemove,
            isActivated: false,
            isFavoriteChanged: false,
          },
        ],
        documentTags: img?.documentType ? [img.documentType] : null,
      };

      await dispatch(updateImageMetadata(backendData));
    }
  };

export const searchDocuments =
  ({ params, signal = null, withoutOnSuccess = false }) =>
  async (dispatch) => {
    dispatch(startSearch(params));

    const result = await dispatch(
      backendCall({
        method: (api) => api.imageHandling.searchDocuments,
        params,
        ...(signal ? { signal } : {}),
        onSuccess: async (data) => {
          if (withoutOnSuccess) {
            return;
          }
          let allImages = data.searchResult;
          data.images = allImages;
          data.documents = allImages;
          const images = await generateImages(data, (d) => dispatch(generateImageURL(d)));
          data.generateImages = images;
          dispatch(search(data));

          await dispatch(loadImages(images));
        },
        onError: (e) => {
          console.error(e);
          dispatch(search({}));
        },
      })
    );

    return result;
  };

export const updateImageBlob = (data) => (dispatch, getState) => {
  dispatch(updateImageBlobAction(data));
  const cachedImagesForOP = getState()?.operatorPanel?.imagesDocuments;
  const indexForUpdateCachedImagesOP = cachedImagesForOP.findIndex((i) => i.id === data.id);

  if (indexForUpdateCachedImagesOP >= 0) {
    const updatedCachedImagesForOP = cachedImagesForOP.map((i, index) => (index === indexForUpdateCachedImagesOP ? { ...i, ...data } : i));
    dispatch(setImagesDocuments(updatedCachedImagesForOP));
  }
};

export const setImageContext = (state) => (dispatch) => {
  dispatch(setImageContextAction(state));
};

export default createReducer(initState, {
  [startSearch]: (state, action) => {
    return {
      ...state,
      loaded: false,
      cleared: false,
      context: action.payload,
    };
  },
  [updateSasAction]: (state, action) => ({
    ...state,
    ...action.payload,
  }),
  [search]: (state, action) => {
    const { documentsObject: payloadDocumentsObject, ...restPayload } = action.payload;

    // CRITICAL FIX: Preserve existing base64 data when merging new images
    const mergedImages =
      action.payload.generateImages?.map((newImage) => {
        // Find existing image with same ID
        const existingImage = state.images?.find((img) => img.id === newImage.id);

        if (existingImage && (existingImage.base64 || existingImage.base64Original)) {
          // Preserve the base64 data from existing image
          return {
            ...newImage,
            base64: existingImage.base64 || newImage.base64,
            base64Original: existingImage.base64Original || newImage.base64Original,
          };
        }

        return newImage;
      }) || [];

    const result = {
      ...state,
      loaded: true,
      cleared: false,
      ...restPayload,
      images: mergedImages,
      // Preserve existing documentsObject instead of overwriting with payload
      documentsObject: state.documentsObject || {},
      sasExpiredAt: moment().add(action.payload.sasTokenLifespanMinutes, 'minutes'),
    };
    return result;
  },
  [updateImageBlobAction]: (state, action) => {
    const newState = {
      ...state,
      images: state.images.map((i) => {
        if (i.id === action.payload.id) {
          const updatedImage = {
            ...i,
            ...action.payload,
          };
          return updatedImage;
        }
        return i;
      }),
    };

    return newState;
  },
  [setImages]: (state, action) => {
    return {
      ...state,
      images: action.payload,
      cleared: false, // Reset cleared flag when new images are set
    };
  },
  [setDocuments]: (state, action) => {
    return {
      ...state,
      documentsObject: action.payload,
    };
  },
  [setNotes]: (state, action) => ({
    ...state,
    notes: { ...state.notes, data: action.payload },
  }),
  [setSelectedNote]: (state, action) => ({
    ...state,
    notes: { ...state.notes, selectedNote: action.payload },
  }),
  [updateNotification]: (state, action) => ({
    ...state,
    notification: action.payload,
  }),
  [addNewTempID]: (state, action) => ({
    ...state,
    queue: {
      ...state.queue,
      [action.payload]: {
        actions: [],
        backendId: null,
      },
    },
  }),
  [connectBackendIDtoTempID]: (state, action) => {
    if (!state.queue || !state.queue[action.payload.tempID]) {
      return state;
    }
    return {
      ...state,
      queue: {
        ...state.queue,
        [action.payload.tempID]: {
          ...state.queue[action.payload.tempID],
          backendId: action.payload.id,
        },
      },
    };
  },
  [addActionToQueue]: (state, action) => {
    if (!state.queue || !state.queue[action.payload.tempID]) {
      return state;
    }
    return {
      ...state,
      queue: {
        ...state.queue,
        [action.payload.tempID]: {
          ...state.queue[action.payload.tempID],
          actions: [...state.queue[action.payload.tempID].actions, action.payload.action],
        },
      },
    };
  },
  [addToRotatePull]: (state, action) => ({
    ...state,
    rotatePull: [...state.rotatePull, action.payload],
  }),
  [deleteFromRotatePull]: (state, action) => ({
    ...state,
    rotatePull: state.rotatePull.filter((i) => i !== action.payload),
  }),
  [setDocumentCategories]: (state, action) => ({
    ...state,
    documentCategories: {
      items: action.payload,
      loaded: true,
    },
  }),
  [setPreImageHandlingScreenAction]: (state, action) => ({
    ...state,
    preImageHandlingScreen: action.payload,
  }),
  [setShouldAbortFetchingImagesAction]: (state, action) => ({
    ...state,
    shouldAbortFetchingImages: action.payload,
  }),
  [setImageStripFinished]: (state, action) => {
    return {
      ...state,
      imageStripFinished: action.payload,
    };
  },
  [setUpdatingImageMetadataInProgress]: (state, action) => ({
    ...state,
    updatingImageMetadataInProgress: action.payload,
  }),
  [setImageContextAction]: (state, action) => ({
    ...state,
    context: action.payload,
  }),
  [clearState]: (state) => {
    return {
      ...state,
      images: [],
      documentsObject: {}, // Main store should clear documentsObject
      rotatePull: [],
      loaded: false,
      imageStripFinished: false,
      documentCategories: { items: [], loaded: false },
      cleared: true,
      updatingImageMetadataInProgress: false,
      preImageHandlingScreen: state.preImageHandlingScreen,
      shouldAbortFetchingImages: state.shouldAbortFetchingImages,
    };
  },
  [clearStateKeepImages]: (state) => {
    return {
      ...state,
      // Don't clear documentsObject for operator panel - preserve it to avoid conflicts
      // documentsObject: {},  // This was causing operator panel to lose its context
      rotatePull: [],
      loaded: false,
      imageStripFinished: false,
      documentCategories: { items: [], loaded: false },
      cleared: true,
      updatingImageMetadataInProgress: false,
      preImageHandlingScreen: state.preImageHandlingScreen,
      shouldAbortFetchingImages: state.shouldAbortFetchingImages,
    };
  },
  [clear]: (state) => ({
    ...state,
    documentsObject: {},
    images: [],
    rotatePull: [],
    loaded: false,
    imageStripFinished: false,
    updatingImageMetadataInProgress: false,
    documentCategories: { items: [], loaded: false },
    preImageHandlingScreen: state.preImageHandlingScreen,
    shouldAbortFetchingImages: state.shouldAbortFetchingImages,
  }),
});
